import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCalendarPlus, faPenToSquare, faWallet, faCalendarXmark, faUserPen } from '@fortawesome/free-solid-svg-icons';
import { formatDubai } from '../../../utils/dubaiTime';
import './clientRequests.css';
import {
    isPaymentInReview,
    paymentInReviewMessage,
    isPendingDrift,
    driftCaptureTotal,
    pendingDriftMessage,
    paymentErrorMessage,
    readCancelSettlement,
    readCancelErrorSettlement,
    CANCEL_UNCONFIRMED_MESSAGE,
    isReviewHeldRow,
    REVIEW_HELD_NOTE,
    REVIEW_HELD_OTHER_BOOKINGS_NOTE,
} from '../paymentErrors';
import { clientFundingSummary } from './requestFundingLegs';
import WithdrawRequestModal from './WithdrawRequestModal';

const TYPE_META = {
    create_property: { label: 'New Property', icon: faBuilding },
    edit_property: { label: 'Edit Property', icon: faPenToSquare },
    create_booking: { label: 'New Booking', icon: faCalendarPlus },
    edit_booking: { label: 'Edit Booking', icon: faPenToSquare },
    cancel_booking: { label: 'Cancel Booking', icon: faCalendarXmark },
    edit_client_profile: { label: 'Edit Account', icon: faUserPen },
    edit_client_tasks: { label: 'Edit Tasks (all units)', icon: faPenToSquare },
};

const PAYMENT_METHOD_LABELS = { bank_transfer: 'Bank Transfer', online_payment: 'Online Payment' };
const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
    pending: { label: 'Pending review', className: 'creq-chip--pending' },
    // Card-staged request whose paypage hasn't captured yet (born
    // 'awaiting_payment' server-side, 2026-08-06) — the client sees it,
    // staff don't until it pays.
    awaiting_payment: { label: 'Not submitted — awaiting payment', className: 'creq-chip--payment' },
    approved: { label: 'Approved', className: 'creq-chip--approved' },
    rejected: { label: 'Rejected', className: 'creq-chip--rejected' },
    cancelled: { label: 'Cancelled', className: 'creq-chip--cancelled' },
};

// Asia/Dubai render (item 8) — "19 Jul 2026" (previously the device's locale
// AND timezone decided what showed here).
function formatDate(dateString) {
    if (!dateString) return '—';
    return formatDubai(dateString, 'd MMM yyyy') || '—';
}

const CHANGE_LABELS = {
    booking_start: 'time',
    duration_hours: 'duration',
    required_cleaners: 'cleaners',
    notes: 'notes',
    bedrooms: 'bedrooms',
    bathrooms: 'bathrooms',
    single_beds: 'beds',
    double_beds: 'beds',
    super_king_beds: 'beds',
    property_access: 'access',
    wifi_name: 'wifi',
    wifi_password: 'wifi',
    address_line_1: 'building',
    unit_number: 'unit',
    area: 'area',
    is_beach_towel: 'amenities',
    is_dolce_gusto: 'amenities',
    is_nespresso: 'amenities',
    type_id: 'service',
    property_id: 'property',
    client_type: 'account type',
    company_name: 'company',
    contact_number: 'contact number',
    trn_number: 'TRN',
    is_vat_registered: 'VAT status',
    city: 'city',
    state: 'state',
    country: 'country',
    documents: 'documents',
};

// Where a request row should go — for EVERY status, so the whole row is
// clickable, not just approved ones (2026-07-21). Approved → the created/
// changed entity; pending/rejected/withdrawn → the request-details page (new
// bookings) or the target record being changed (edits). Returns null only when
// there is genuinely nowhere to go (a not-yet-created property).
function clientDestination(request) {
    const type = request.request_type;
    const id = request.result_id || request.target_id;
    if (type === 'create_booking') {
        // Approved → the booking; otherwise the request-details page (which holds
        // everything requested + withdraw / complete-payment).
        return request.status === 'approved' && request.result_id
            ? `/client/bookings/${request.result_id}`
            : `/client/bookings/requests/${request.id}`;
    }
    if (type === 'edit_booking' || type === 'cancel_booking') {
        // An approved non-last-minute cancellation deletes the booking → nowhere.
        if (type === 'cancel_booking' && request.status === 'approved'
            && !request.payload?.is_last_minute_at_request) return null;
        return id ? `/client/bookings/${id}` : null;
    }
    if (type === 'create_property') {
        return request.status === 'approved' && request.result_id
            ? `/client/properties/${request.result_id}` : null; // not created until approved
    }
    if (type === 'edit_property') return id ? `/client/properties/${id}` : null;
    if (type === 'edit_client_profile') return '/client/profile';
    if (type === 'edit_client_tasks') return '/client/tasks';
    return null;
}

// The record a request references, for the row's reference chip. Booking # /
// Property # from result_id||target_id; Client # for account & all-units edits.
function clientEntityRef(request) {
    const type = request.request_type;
    const id = request.result_id || request.target_id;
    if (['create_booking', 'edit_booking', 'cancel_booking'].includes(type)) {
        return id ? `Booking #${id}` : null;
    }
    if (['create_property', 'edit_property'].includes(type)) {
        return id ? `Property #${id}` : null;
    }
    if (['edit_client_profile', 'edit_client_tasks'].includes(type)) {
        return request.client_id ? `Client #${request.client_id}` : null;
    }
    return null;
}

function requestSummary(request) {
    const p = request.payload || {};
    if (request.request_type === 'create_property') {
        return `Unit ${p.unit_number || ''} · ${p.address_line_1 || ''} · ${p.bedrooms === 0 ? 'Studio' : `${p.bedrooms} BR`}`;
    }
    if (request.request_type === 'create_booking') {
        return `${p.property_label || ''} · ${p.type_label || ''} · ${formatDubai(p.booking_start, 'd MMM yyyy, HH:mm') || ''}`;
    }
    if (request.request_type === 'edit_booking' || request.request_type === 'edit_property') {
        const fields = Array.isArray(p.changed_fields) ? p.changed_fields : [];
        const labels = [...new Set(fields.map((f) => CHANGE_LABELS[f] || f))];
        const what = labels.length ? `changing ${labels.join(', ')}` : 'change request';
        return `${p.property_label || ''} · ${what}`;
    }
    if (request.request_type === 'cancel_booking') {
        return `${p.property_label || ''} · ${formatDubai(p.booking_start, 'd MMM yyyy, HH:mm') || ''} · cancellation`;
    }
    if (request.request_type === 'edit_client_profile') {
        const fields = Array.isArray(p.changed_fields) ? p.changed_fields : [];
        const labels = [...new Set(fields.map((f) => CHANGE_LABELS[f] || f))];
        return `Account details · ${labels.length ? `changing ${labels.join(', ')}` : 'change request'}`;
    }
    if (request.request_type === 'edit_client_tasks') {
        const n = Array.isArray(p.tasks) ? p.tasks.length : 0;
        return `Tasks for all units · ${n} task${n === 1 ? '' : 's'} after change`;
    }
    return '';
}

// "Your Requests" card — embedded on the client Home page (chomeCard design).
// Renders nothing when the feature is off (503) or the client has no requests.
//
// Controlled component: the parent (ClientHome) fetches `requests` as part of the
// page's single loading gate and passes them in, so this card paints in the same
// frame as the rest of the dashboard instead of popping in separately. `onReload`
// lets the parent refresh after a client cancels one of their requests.
const PAGE_SIZE = 4; // keeps the home section compact (2026-07-05; 6→4 on 2026-07-12)

export default function ClientRequestsCard({ requests = [], deposits = [], onReload }) {
    const navigate = useNavigate();
    const [cancellingId, setCancellingId] = useState(null);
    // The request whose withdraw-confirm modal is open (null = closed).
    const [withdrawTarget, setWithdrawTarget] = useState(null);
    // Single in-flight guard for the payment rails on a row. Without it a
    // double-click fires two concurrent /payments/retry calls, and the
    // server-side dedupe scan can lose that race — two live paypages for one
    // booking. Siblings (ClientWallet, BookingRequestDetails) already guard.
    const [payActionId, setPayActionId] = useState(null);
    // Persistent per-row money notice (amount changed / payment under gateway
    // review) — these are NOT errors and must not be a disappearing alert.
    const [payNotice, setPayNotice] = useState(null); // { id, text }
    // Fresh figure quoted by PENDING_DRIFT and shown to the client, per row —
    // resent as expected_total so the next tap pays the acknowledged amount.
    const [ackTotals, setAckTotals] = useState({}); // { [requestId]: number }
    const [page, setPage] = useState(1);

    // Deposits and requests share ONE paginated list so the home card shows at
    // most PAGE_SIZE items total — pending deposits COUNT toward the 4 (the owner
    // 2026-07-16), rather than being stacked on top of a full page of requests.
    //
    // ORDERING (2026-07-18): strictly chronological, NEWEST FIRST — no
    // type-based grouping. Concatenating the two arrays used to push every
    // deposit above every request regardless of age, so a top-up from last week
    // sat above the booking request made a minute ago. Newest-first is what the
    // rest of the client app already does: GET /requests/my and the deposits
    // list both come back `ORDER BY created_at DESC`, and the wallet
    // transactions list is newest-first too. (The ADMIN queue is deliberately
    // the opposite — oldest-first FIFO — because staff work a backlog.)
    const items = useMemo(() => ([
        ...deposits.map((d) => ({ kind: 'deposit', key: `dep-${d.id}`, at: d.created_at, dep: d })),
        ...requests.map((r) => ({ kind: 'request', key: `req-${r.id}`, at: r.created_at, req: r })),
    ]).sort((a, b) => {
        // Missing/!unparseable timestamps sort last rather than randomising the
        // whole list via NaN comparisons.
        const ta = new Date(a.at).getTime();
        const tb = new Date(b.at).getTime();
        const va = Number.isNaN(ta) ? -Infinity : ta;
        const vb = Number.isNaN(tb) ? -Infinity : tb;
        return vb - va;
    }), [deposits, requests]);

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    // Clamp during render (not just in a post-commit effect) so a shrinking list
    // can never flash an empty card body for a frame before the effect corrects it.
    const effectivePage = Math.min(page, totalPages);

    // Persist the corrected page so `page` state stays in sync with what's shown.
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const pageStart = (effectivePage - 1) * PAGE_SIZE;
    const visibleItems = items.slice(pageStart, pageStart + PAGE_SIZE);

    // The withdraw itself — no window.confirm / window.alert. WithdrawRequestModal
    // owns the confirm preview (how much comes back) and the result screen.
    // Resolves to the server body (for released_to_wallet) or throws an Error
    // with a client-safe message that the modal renders inline.
    const performWithdraw = async (request) => {
        try {
            const res = await axios.post(`/api/client-requests/requests/${request.id}/cancel`);
            if (onReload) await onReload();
            return res.data;
        } catch (err) {
            if (onReload) await onReload();
            // Withdraw refuses while the gateway still holds money for this
            // request — surface it, don't pretend it worked.
            if (isPaymentInReview(err)) {
                throw new Error(paymentInReviewMessage(err));
            }
            console.error('Error cancelling request:', err);
            // e.g. staff approved it moments ago — say so; onReload already
            // refreshed the list to reality behind the modal.
            throw new Error(paymentErrorMessage(err, 'Could not withdraw this request. Please refresh and try again.'));
        }
    };

    // "Cancel" on an awaiting-payment row cancels the CHECKOUT (which releases
    // the staged request server-side) — not the generic request-withdraw.
    const handleCancelPayment = async (request) => {
        if (payActionId || cancellingId) return;
        // Conditional wording: whether anything was charged is only known after
        // the server checks the gateway (it may come back 'paid' or in review).
        if (!window.confirm('Cancel this payment? If it hasn’t been completed, nothing will be charged and the booking request will not be submitted.')) return;
        setPayActionId(request.id);
        setCancellingId(request.id);
        setPayNotice(null);
        const paidText = 'This payment had already gone through, so it could not be cancelled — '
            + 'your payment was received and the booking request has been submitted for review. '
            + 'Please don’t request this booking again.';
        try {
            const res = await axios.post('/api/payments/cancel', { order_ref: request.order_ref });
            // ONE reader decides (shared with mobile). 'paid' = the payment had
            // actually gone through (lost redirect) and this call settled it.
            // The client WAS charged and the request WAS submitted — say so
            // instead of letting the row silently disappear. Fail CLOSED:
            // anything but an explicit not-paid outcome is "still confirming".
            const settlement = readCancelSettlement(res);
            if (settlement === 'paid') {
                setPayNotice({ id: request.id, text: paidText });
            } else if (settlement !== 'cancelled') {
                setPayNotice({ id: request.id, text: CANCEL_UNCONFIRMED_MESSAGE });
            }
            if (onReload) await onReload();
        } catch (err) {
            const settlement = readCancelErrorSettlement(err);
            if (settlement === 'review') {
                setPayNotice({ id: request.id, text: paymentInReviewMessage(err) });
            } else if (settlement === 'paid') {
                setPayNotice({ id: request.id, text: paidText });
            } else if (settlement === 'unconfirmed' && err.response) {
                // Bare 409/503/5xx about this checkout: ambiguous about money.
                setPayNotice({ id: request.id, text: CANCEL_UNCONFIRMED_MESSAGE });
            } else if (settlement === 'unconfirmed') {
                console.error('Error cancelling payment:', err);
                window.alert(paymentErrorMessage(err, 'Could not cancel this payment. Please refresh and try again.'));
            }
            if (onReload) await onReload();
        } finally {
            setCancellingId(null);
            setPayActionId(null);
        }
    };

    // "Complete payment" must NEVER be a dead button (2026-07-18).
    // ALWAYS mint a fresh paypage through /payments/retry: the stored
    // payment_url points at an N-Genius paypage session that expires
    // server-side, so reopening it minutes later lands on a dead gateway page
    // (2026-08-06 — "resume payment never worked").
    const handleCompletePayment = async (request) => {
        if (payActionId || cancellingId) return;
        const ackTotal = ackTotals[request.id];
        setPayActionId(request.id);
        setPayNotice(null);
        try {
            // No order_ref means there is no checkout to retry at all. Say so
            // persistently instead of firing a request that 400s into silence.
            if (!request.order_ref) {
                setPayNotice({
                    id: request.id,
                    text: 'We can’t reopen this payment page. Please refresh — if it still doesn’t open, '
                        + 'withdraw this request and book again. Nothing is charged twice.',
                });
                return;
            }
            const body = { order_ref: request.order_ref };
            if (ackTotal != null) body.expected_total = ackTotal;
            const res = await axios.post('/api/payments/retry', body);
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // navigating away — keep the guard on
            }
            // 200 without a URL: persistent, never a vanishing toast.
            setPayNotice({
                id: request.id,
                text: 'We couldn’t open the secure payment page just now. Please try again in a moment — '
                    + 'nothing has been charged for this attempt.',
            });
        } catch (err) {
            // Amount moved: show the fresh figure and remember it, so the next
            // tap pays the acknowledged amount rather than dead-ending forever.
            if (isPendingDrift(err)) {
                const fresh = driftCaptureTotal(err);
                if (fresh != null) setAckTotals((prev) => ({ ...prev, [request.id]: fresh }));
                setPayNotice({ id: request.id, text: pendingDriftMessage(err) });
            } else if (isPaymentInReview(err)) {
                setPayNotice({ id: request.id, text: paymentInReviewMessage(err) });
            } else {
                window.alert(paymentErrorMessage(err, 'Could not open the payment page. Please try again.'));
            }
        } finally {
            setPayActionId(null);
        }
    };

    if (requests.length === 0 && deposits.length === 0) return null;

    const subParts = [];
    if (requests.length) subParts.push(`${requests.length} request${requests.length === 1 ? '' : 's'}`);
    if (deposits.length) subParts.push(`${deposits.length} pending deposit${deposits.length === 1 ? '' : 's'}`);

    return (
        <div className="chomeCard chomeCard--wide">
            <div className="chomeCard__head">
                <div>
                    <h3 className="chomeCard__title">Your Requests</h3>
                    <div className="chomeCard__sub">
                        {subParts.join(' · ')} awaiting our team&apos;s review
                    </div>
                </div>
            </div>

            <div className="creq-home-list">
                {visibleItems.map((item) => {
                    if (item.kind === 'deposit') {
                        const dep = item.dep;
                        return (
                            <div className="creq-home-item" key={item.key}>
                                <div className="creq-home-item-icon creq-home-item-icon--deposit">
                                    <FontAwesomeIcon icon={faWallet} />
                                </div>
                                <div className="creq-home-item-main">
                                    <div className="creq-home-item-title">
                                        <strong>Deposit</strong>
                                        <span className="creq-chip creq-chip--pending">Pending review</span>
                                    </div>
                                    <div className="creq-home-item-summary">
                                        {money(dep.amount)}
                                        {Number(dep.bonus_amount) > 0 ? ` (+${money(dep.bonus_amount)} bonus)` : ''}
                                        {' · '}{PAYMENT_METHOD_LABELS[dep.payment_method] || dep.payment_method}
                                    </div>
                                    <div className="creq-home-item-meta">Submitted {formatDate(dep.created_at)}</div>
                                </div>
                            </div>
                        );
                    }

                    const request = item.req;
                    const type = TYPE_META[request.request_type] || { label: request.request_type, icon: faPenToSquare };
                    const status = STATUS_META[request.status] || { label: request.status, className: '' };
                    const entityRef = clientEntityRef(request);
                    // Pending create_booking rows open the booking-style request
                    // details page (everything requested + withdraw / complete
                    // payment live in there).
                    // 'awaiting_payment' rows are LIVE payment rows — they carry
                    // the resume/cancel pair exactly like a payment_state-flagged
                    // pending row (born-staged model, 2026-08-06).
                    const isPendingBooking = request.request_type === 'create_booking'
                        && ['pending', 'awaiting_payment'].includes(request.status);
                    // Review-held: the bank is holding an authorised payment for
                    // THIS request. The stored paypage is consumed, so there is
                    // no Pay action, and withdrawing would force us to guess
                    // about their money — so no Cancel either. Calm + standing.
                    const reviewHeld = isPendingBooking && isReviewHeldRow(request);
                    const awaitingPayment = isPendingBooking && !reviewHeld && request.payment_state === 'awaiting_payment';
                    // A6 — how this request is/was paid, straight from the
                    // server's funding_legs (same list the admin review shows),
                    // client-phrased. '' when there is nothing truthful to say.
                    const fundingLine = clientFundingSummary(request);
                    // Whole row is clickable for every status now — approved goes
                    // to the entity, pending/rejected to the request or the target.
                    const dest = clientDestination(request);
                    const open = dest ? () => navigate(dest) : null;
                    return (
                        <div
                            className={`creq-home-item${open ? ' creq-home-item--link' : ''}`}
                            key={request.id}
                            role={open ? 'button' : undefined}
                            tabIndex={open ? 0 : undefined}
                            title={open ? 'View' : undefined}
                            onClick={open || undefined}
                            onKeyDown={open ? (e) => { if (e.key === 'Enter' || e.key === ' ') open(); } : undefined}
                        >
                            <div className="creq-home-item-icon">
                                <FontAwesomeIcon icon={type.icon} />
                            </div>
                            <div className="creq-home-item-main">
                                <div className="creq-home-item-title">
                                    <strong>{type.label}</strong>
                                    <span className="creq-req-id" title="Request reference">REQ-{request.id}</span>
                                    {entityRef && (
                                        <span className="creq-entity-ref" title="Opens this record">{entityRef}</span>
                                    )}
                                    {reviewHeld ? (
                                        <span className="creq-chip creq-chip--review" title="The bank is reviewing this card payment">
                                            Payment in review
                                        </span>
                                    ) : awaitingPayment ? (
                                        <span className="creq-chip creq-chip--payment" title="Not sent to our team yet — submitted as soon as the card payment is completed">
                                            Not submitted — awaiting payment
                                        </span>
                                    ) : (
                                        <span className={`creq-chip ${status.className}`}>{status.label}</span>
                                    )}
                                    {/* Sent by the server only while the request is
                                        still pending (bookingNotice.js). */}
                                    {request.booking_notice?.last_minute && (
                                        <span className="creq-chip creq-chip--lastminute" title={request.booking_notice.last_minute_note || undefined}>
                                            Last-minute
                                        </span>
                                    )}
                                </div>
                                <div className="creq-home-item-summary">{requestSummary(request)}</div>
                                {request.booking_notice?.office_hours_note && (
                                    <div className="creq-home-item-officehours">{request.booking_notice.office_hours_note}</div>
                                )}
                                <div className="creq-home-item-meta">
                                    Requested {formatDate(request.created_at)}
                                    {request.reviewed_at ? ` · Reviewed ${formatDate(request.reviewed_at)}` : ''}
                                </div>
                                {fundingLine && (
                                    <div className="creq-home-item-funding">{fundingLine}</div>
                                )}
                                {request.request_type === 'create_booking' && request.status === 'approved'
                                    && request.final_total != null && request.estimated_total != null
                                    && Math.abs(Number(request.final_total) - Number(request.estimated_total)) > 0.01 && (
                                    <div className="creq-home-item-pricechange">
                                        Price {Number(request.final_total) > Number(request.estimated_total) ? 'increased' : 'decreased'} on approval:
                                        {' '}requested <strong>{money(request.estimated_total)}</strong> → approved <strong>{money(request.final_total)}</strong>
                                    </div>
                                )}
                                {request.status === 'rejected' && request.review_notes && (
                                    <div className="creq-home-item-reason">
                                        <strong>Reason:</strong> {request.review_notes}
                                    </div>
                                )}
                                {/* The chip already says "Not submitted" — this
                                    line only adds the TRUE capture figure when
                                    pending invoices ride along, so the funding
                                    line's booking portion can't read as the
                                    whole charge (2026-08-06). */}
                                {awaitingPayment
                                    && Number(request.payment_capture_total) > 0.009
                                    && Number(request.payment_capture_total) - Number(request.estimated_total || 0) > 0.01 && (
                                    <div className="creq-home-item-payNotice">
                                        Total to pay: <strong>AED {Number(request.payment_capture_total).toFixed(2)}</strong> — includes your pending invoices.
                                    </div>
                                )}
                                {/* Persistent money notice for this row — the
                                    amount changed, the payment is under gateway
                                    review, or it turned out to be already paid. */}
                                {payNotice && payNotice.id === request.id && (
                                    <div className="creq-home-item-payNotice">{payNotice.text}</div>
                                )}
                                {/* Review-held: standing explanation, no action.
                                    Explicitly says other bookings still work —
                                    the thing a blocked client most needs to hear. */}
                                {reviewHeld && (
                                    <div className="creq-home-item-reviewNotice">
                                        {REVIEW_HELD_NOTE}
                                        <div className="creq-home-item-reviewSub">{REVIEW_HELD_OTHER_BOOKINGS_NOTE}</div>
                                    </div>
                                )}
                            </div>
                            {reviewHeld ? null : awaitingPayment ? (
                                <div className="creq-home-item-payActions">
                                    <button
                                        className="creq-btn creq-btn--primary creq-btn--sm"
                                        onClick={(e) => { e.stopPropagation(); handleCompletePayment(request); }}
                                        disabled={!!payActionId || cancellingId === request.id}
                                    >
                                        {payActionId === request.id && cancellingId !== request.id
                                            ? 'Opening secure page…'
                                            : (ackTotals[request.id] != null ? 'Pay updated amount' : 'Complete payment')}
                                    </button>
                                    <button
                                        className="creq-btn creq-btn--ghost creq-btn--sm"
                                        onClick={(e) => { e.stopPropagation(); handleCancelPayment(request); }}
                                        disabled={!!payActionId || cancellingId === request.id}
                                    >
                                        {cancellingId === request.id ? 'Cancelling…' : 'Cancel'}
                                    </button>
                                </div>
                            ) : request.status === 'pending' && (
                                <button
                                    className="creq-btn creq-btn--ghost creq-btn--sm"
                                    onClick={(e) => { e.stopPropagation(); setWithdrawTarget(request); }}
                                >
                                    Cancel
                                </button>
                            )}
                            {open && <span className="creq-home-item-chev" aria-hidden="true">›</span>}
                        </div>
                    );
                })}
            </div>

            {totalPages > 1 && (
                <div className="creq-home-pagination">
                    <button
                        type="button"
                        className="creq-btn creq-btn--ghost creq-btn--sm"
                        onClick={() => setPage(Math.max(1, effectivePage - 1))}
                        disabled={effectivePage === 1}
                    >
                        Previous
                    </button>
                    <span className="creq-home-page-info">Page {effectivePage} of {totalPages}</span>
                    <button
                        type="button"
                        className="creq-btn creq-btn--ghost creq-btn--sm"
                        onClick={() => setPage(Math.min(totalPages, effectivePage + 1))}
                        disabled={effectivePage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}

            {withdrawTarget && (
                <WithdrawRequestModal
                    request={withdrawTarget}
                    onConfirm={performWithdraw}
                    onClose={() => setWithdrawTarget(null)}
                />
            )}
        </div>
    );
}
